// Implements actual API calls to the backend

import axiosInstance from "../utils/axios";
import { getCompanyIdFromToken } from "../utils/tokenUtils";
import { convertToUTC } from "../utils/dateUtils";

// API Endpoints
const API_ENDPOINTS = {
  QUERIES: "api/Query",
};


// Update query status
export const updateQueryStatus = async (queryId, status) => {
  try {
    // Validate required parameters
    if (!queryId) {
      throw new Error("Query ID is required");
    }
    if (status === undefined || status === null) {
      throw new Error("Status is required");
    }
    if (status < 1 || status > 3) {
      throw new Error("Status must be between 1 and 3 (1=Open, 2=In Progress, 3=Closed)");
    }

    // Get CompanyId from JWT token
    const companyId = getCompanyIdFromToken();

    // Prepare the payload according to the API specification
    const apiPayload = {
      queryId: queryId,
      status: status,
      ...(companyId && { companyId }), // Include companyId if available
    };

    // Make API call using axios instance with POST method
    const response = await axiosInstance.post(
      `${API_ENDPOINTS.QUERIES}/UpdateQueryStatus`,
      apiPayload,
      {
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
      },
    );

    // Validate response status
    if (response.status < 200 || response.status >= 300) {
      throw new Error(
        `HTTP Error: ${response.status} - ${response.statusText}`,
      );
    }

    // Validate response data structure
    if (!response.data) {
      throw new Error("Invalid response: No data received");
    }

    const { data, message, statusCode, status: responseStatus, totalRecords } = response.data;

    // Validate expected response structure (case-insensitive check for status)
    if (statusCode !== 200 || (responseStatus?.toLowerCase() !== "success")) {
      throw new Error(message || "API returned unsuccessful status");
    }

    // Parse the data field from string to JSON if needed
    let parsedData = null;
    try {
      parsedData = typeof data === "string" ? JSON.parse(data) : data;
    } catch (parseError) {
      console.error("Error parsing update query status response data:", parseError);
      // Don't throw error for parsing issues, just use raw data
      parsedData = data;
    }

    return {
      success: true,
      data: parsedData,
      message: message || "Query status updated successfully",
      totalRecords: totalRecords || 1,
    };
  } catch (error) {
    console.error("Error updating query status:", error);

    // Handle different types of errors
    let errorMessage = "Failed to update query status";

    if (error.response) {
      // Server responded with error status
      const status = error.response.status;
      const serverMessage =
        error.response.data?.message || error.response.data?.error;

      if (status >= 400 && status < 500) {
        // Client errors (4xx)
        errorMessage = serverMessage || `Client error: ${status}`;
      } else if (status >= 500) {
        // Server errors (5xx)
        errorMessage = serverMessage || `Server error: ${status}`;
      }
    } else if (error.request) {
      // Network error - no response received
      errorMessage = "Network error: Unable to connect to server";
    } else {
      // Other errors (validation, parsing, etc.)
      errorMessage = error.message || "Unknown error occurred";
    }

    return {
      success: false,
      error: errorMessage,
      data: null,
    };
  }
};

// ===== QUERY OPERATIONS =====

// Get all queries with optional filtering and pagination
export const getQueries = async (
  filters = {},
  pagination = { pageNumber: 1, pageSize: 5 },
) => {
  try {
    // Get CompanyId from JWT token
    const companyId = getCompanyIdFromToken();

    // Map frontend filters to API parameters
    const apiPayload = {
      categoryId: filters.categoryId || null,
      assignedTo: filters.assignedTo || null,
      status: filters.status || null,
      description: filters.description || null,
      createdDateFrom: filters.createdDateFrom
        ? convertToUTC(filters.createdDateFrom, "00:00")
        : null,
      createdDateTo: filters.createdDateTo
        ? convertToUTC(filters.createdDateTo, "23:59")
        : null,
      updatedDateFrom: filters.updatedDateFrom
        ? convertToUTC(filters.updatedDateFrom, "00:00")
        : null,
      updatedDateTo: filters.updatedDateTo
        ? convertToUTC(filters.updatedDateTo, "23:59")
        : null,
      lastRepliedBy: filters.lastRepliedBy || null,
      feedback: filters.feedback || null,
      search: filters.search || null,
      pageNumber: pagination.pageNumber || 1,
      pageSize: pagination.pageSize || 5,
      sortOrder: filters.sortOrder || 5,
      widgetType: filters.widgetType || null,
      type: filters.type !== undefined && filters.type !== null ? parseInt(filters.type) : 0,
      // Add CompanyId if available
      companyId: companyId,
    };

    // Make API call using axios instance with POST method
    const response = await axiosInstance.post(
      `${API_ENDPOINTS.QUERIES}/GetAllQueries`,
      apiPayload,
    );
    const responseData = response.data;

    // Parse the response based on the API structure (case-insensitive check for status)
    if (responseData.statusCode === 200 && responseData.status?.toLowerCase() === "success") {
      // Parse the data string if it's a JSON string
      let queriesData = [];
      let dashboardData = null;

      if (typeof responseData.data === "string") {
        try {
          const parsedData = JSON.parse(responseData.data);
          // The API returns { Dashboard: "...", Queries: [...] }
          // Extract the Queries array from the parsed object
          if (parsedData && Array.isArray(parsedData.Queries)) {
            queriesData = parsedData.Queries;
          } else if (Array.isArray(parsedData)) {
            queriesData = parsedData;
          } else {
            queriesData = [];
          }

          // Extract Dashboard data (it's a JSON string)
          if (parsedData && parsedData.Dashboard) {
            try {
              dashboardData = typeof parsedData.Dashboard === "string"
                ? JSON.parse(parsedData.Dashboard)
                : parsedData.Dashboard;
            } catch (dashError) {
              console.error("Error parsing dashboard data:", dashError);
            }
          }
        } catch (parseError) {
          console.error("Error parsing queries data:", parseError);
          queriesData = [];
        }
      } else if (Array.isArray(responseData.data)) {
        queriesData = responseData.data;
      } else if (responseData.data && typeof responseData.data === "object") {
        // Handle case where data is already an object with Queries array
        if (Array.isArray(responseData.data.Queries)) {
          queriesData = responseData.data.Queries;
        } else {
          queriesData = [];
        }

        // Extract Dashboard data
        if (responseData.data.Dashboard) {
          try {
            dashboardData = typeof responseData.data.Dashboard === "string"
              ? JSON.parse(responseData.data.Dashboard)
              : responseData.data.Dashboard;
          } catch (dashError) {
            console.error("Error parsing dashboard data:", dashError);
          }
        }
      }

      // Ensure queriesData is always an array
      if (!Array.isArray(queriesData)) {
        queriesData = [];
      }

      console.log("getQueries returning:", { success: true, dataLength: queriesData.length, dashboard: dashboardData });

      return {
        success: true,
        data: queriesData,
        dashboard: dashboardData,
        pagination: {
          pageNumber: pagination.pageNumber,
          pageSize: pagination.pageSize,
          total: responseData.totalRecords || queriesData.length,
          totalPages: Math.ceil(
            (responseData.totalRecords || queriesData.length) /
            pagination.pageSize,
          ),
          hasNext:
            pagination.pageNumber * pagination.pageSize <
            (responseData.totalRecords || queriesData.length),
          hasPrev: pagination.pageNumber > 1,
        },
        filters: filters,
        message: responseData.message,
        totalRecords: responseData.totalRecords,
      };
    } else {
      throw new Error(responseData.message || "Failed to fetch queries");
    }
  } catch (error) {
    console.error("Error fetching queries:", error);

    return {
      success: false,
      error: error.message || "Failed to fetch queries",
      data: [],
      pagination: {
        pageNumber: pagination.pageNumber,
        pageSize: pagination.pageSize,
        total: 0,
        totalPages: 0,
        hasNext: false,
        hasPrev: false,
      },
      filters: filters,
      totalRecords: 0,
    };
  }
};

// Get a single query by ID
export const getQueryById = async (queryId) => {
  try {
    // Get CompanyId from JWT token
    const companyId = getCompanyIdFromToken();

    // Make API call using axios instance with GET method
    const response = await axiosInstance.get(
      `${API_ENDPOINTS.QUERIES}/GetQueryById`,
      {
        params: {
          QueryId: queryId,
          ...(companyId && { companyId }), // Include companyId if available
        },
      },
    );
    const responseData = response.data;

    // Parse the response based on the API structure (case-insensitive check for status)
    if (responseData.statusCode === 200 && responseData.status?.toLowerCase() === "success") {
      // Parse the data string if it's a JSON string
      let queryData = null;
      if (typeof responseData.data === "string") {
        try {
          queryData = JSON.parse(responseData.data);
        } catch (parseError) {
          console.error("Error parsing query data:", parseError);
          queryData = null;
        }
      } else if (responseData.data) {
        queryData = responseData.data;
      }

      return {
        success: true,
        data: queryData,
        message: responseData.message,
      };
    } else {
      throw new Error(responseData.message || "Failed to fetch query");
    }
  } catch (error) {
    console.error("Error fetching query by ID:", error);

    return {
      success: false,
      error: error.message || "Failed to fetch query",
      data: null,
    };
  }
};

// Get all query categories with subcategories
export const getAllQueryCategories = async () => {
  try {
    // Get CompanyId from JWT token
    const companyId = getCompanyIdFromToken();

    // Make API call using axios instance with GET method
    const response = await axiosInstance.get(
      `${API_ENDPOINTS.QUERIES}/GetAllQueryCategories`,
      {
        params: {
          ...(companyId && { companyId }), // Include companyId if available
        },
      },
    );
    const responseData = response.data;

    // Parse the response based on the API structure (case-insensitive check for status)
    if (responseData.statusCode === 200 && responseData.status?.toLowerCase() === "success") {
      // Parse the data string if it's a JSON string
      let categoriesData = [];
      if (typeof responseData.data === "string") {
        try {
          categoriesData = JSON.parse(responseData.data);
        } catch (parseError) {
          console.error("Error parsing categories data:", parseError);
          categoriesData = [];
        }
      } else if (Array.isArray(responseData.data)) {
        categoriesData = responseData.data;
      }

      return {
        success: true,
        data: categoriesData,
        message: responseData.message,
        totalRecords: responseData.totalRecords,
      };
    } else {
      throw new Error(
        responseData.message || "Failed to fetch query categories",
      );
    }
  } catch (error) {
    console.error("Error fetching query categories:", error);

    return {
      success: false,
      error: error.message || "Failed to fetch query categories",
      data: [],
      totalRecords: 0,
    };
  }
};

// Create a new query
export const createQuery = async (queryData) => {
  try {
    // Validate required fields before making API call
    if (!queryData.leadId) {
      throw new Error("Lead ID is required");
    }
    if (!queryData.categoryId) {
      throw new Error("Category ID is required");
    }
    if (!queryData.subject?.trim()) {
      throw new Error("Subject is required");
    }
    if (!queryData.description?.trim()) {
      throw new Error("Description is required");
    }
    if (!queryData.status) {
      throw new Error("Status is required");
    }
    if (!queryData.priority) {
      throw new Error("Priority is required");
    }

    const companyId = getCompanyIdFromToken();

    const apiPayload = {
      leadId: queryData.leadId,
      categoryId: queryData.categoryId,
      subject: queryData.subject.trim(),
      description: queryData.description.trim(),
      status: queryData.status,
      priority: queryData.priority,
      companyId: companyId,
    };

    // Fix the endpoint URL (was "CraeteQuery", should be "CreateQuery")
    const response = await axiosInstance.post(
      `${API_ENDPOINTS.QUERIES}/CreateQuery`,
      apiPayload,
      {
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
      },
    );

    // Validate response status
    if (response.status < 200 || response.status >= 300) {
      throw new Error(
        `HTTP Error: ${response.status} - ${response.statusText}`,
      );
    }

    // Validate response data structure
    if (!response.data) {
      throw new Error("Invalid response: No data received");
    }

    const { data, message, statusCode, status, totalRecords } = response.data;

    // Validate expected response structure (case-insensitive check for status)
    if (statusCode !== 200 || (status?.toLowerCase() !== "success")) {
      throw new Error(message || "API returned unsuccessful status");
    }

    // Parse the data field from string to JSON (same as getAllLeads)
    let parsedQueryData = null;
    try {
      parsedQueryData = typeof data === "string" ? JSON.parse(data) : data;
    } catch (parseError) {
      console.error("Error parsing query creation response data:", parseError);
      throw new Error("Invalid data format received from server");
    }

    return {
      success: true,
      data: parsedQueryData,
      message: message || "Query created successfully",
      totalRecords: totalRecords || 1,
    };
  } catch (error) {
    console.error("Error creating query:", error);

    // Handle different types of errors
    let errorMessage = "Failed to create query";

    if (error.response) {
      // Server responded with error status
      const status = error.response.status;
      const serverMessage =
        error.response.data?.message || error.response.data?.error;

      if (status >= 400 && status < 500) {
        // Client errors (4xx)
        errorMessage = serverMessage || `Client error: ${status}`;
      } else if (status >= 500) {
        // Server errors (5xx)
        errorMessage = serverMessage || `Server error: ${status}`;
      }
    } else if (error.request) {
      // Network error - no response received
      errorMessage = "Network error: Unable to connect to server";
    } else {
      // Other errors (validation, parsing, etc.)
      errorMessage = error.message || "Unknown error occurred";
    }

    return {
      success: false,
      error: errorMessage,
      data: null,
    };
  }
};

// Get all leads for query creation with pagination and search
export const getAllLeads = async (payload = { pageNumber: 1, pageSize: 10, search: "" }) => {
  try {
    const response = await axiosInstance.post("/api/Query/GetAllLeads", payload, {
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
       showLoader: false
    });

    // Validate response status
    if (response.status < 200 || response.status >= 300) {
      throw new Error(
        `HTTP Error: ${response.status} - ${response.statusText}`,
      );
    }

    // Validate response structure
    if (!response.data) {
      throw new Error("Invalid response: No data received");
    }

    const { data, message, statusCode, status, totalRecords } = response.data;

    // Validate expected response structure (case-insensitive check for status)
    if (statusCode !== 200 || (status?.toLowerCase() !== "success")) {
      throw new Error(message || "API returned unsuccessful status");
    }

    // Parse the data field from string to JSON
    let parsedLeads = [];
    try {
      parsedLeads = typeof data === "string" ? JSON.parse(data) : data;
    } catch (parseError) {
      console.error("Error parsing leads data:", parseError);
      throw new Error("Invalid data format received from server");
    }

    // Validate parsed data is an array
    if (!Array.isArray(parsedLeads)) {
      throw new Error("Expected leads data to be an array");
    }

    return {
      success: true,
      data: parsedLeads,
      totalRecords: totalRecords || parsedLeads.length,
      message: message || "Leads fetched successfully",
    };
  } catch (error) {
    console.error("Error fetching leads:", error);

    // Handle different types of errors
    let errorMessage = "Failed to fetch leads";

    if (error.response) {
      // Server responded with error status
      const status = error.response.status;
      const serverMessage =
        error.response.data?.message || error.response.data?.error;

      if (status >= 400 && status < 500) {
        // Client errors (4xx)
        errorMessage = serverMessage || `Client error: ${status}`;
      } else if (status >= 500) {
        // Server errors (5xx)
        errorMessage = serverMessage || `Server error: ${status}`;
      }
    } else if (error.request) {
      // Network error - no response received
      errorMessage = "Network error: Unable to connect to server";
    } else {
      // Other errors (parsing, validation, etc.)
      errorMessage = error.message || "Unknown error occurred";
    }

    return {
      success: false,
      error: errorMessage,
      data: [],
      totalRecords: 0,
    };
  }
};

/**
 * Get queries by Lead ID using POST request
 * @param {string} leadId - The Lead ID to fetch queries for
 * @param {Object} options - Optional filtering and pagination options
 * @param {string} options.status - Filter by status
 * @param {string} options.priority - Filter by priority
 * @param {string} options.sortOrder - Sort order for results
 * @param {number} options.pageNumber - Page number for pagination (1-based, default: 1)
 * @param {number} options.pageSize - Number of items per page (default: 10, use 0 for all)
 * @returns {Promise<Object>} Response object with success, data, message, and totalRecords
 */
export const getQueriesByLeadId = async (leadId, options = {}) => {
  try {
    // Validate required parameter
    if (!leadId) {
      throw new Error("Lead ID is required");
    }

    // Prepare request body for POST request
    const requestBody = {
      leadId: leadId,
      status: options.status || "",
      priority: options.priority || "",
      sortOrder: options.sortOrder || "",
      pageNumber: options.pageNumber || 1,
      pageSize: options.pageSize || 10
    };


    // Make API call using axios instance with POST method
    const response = await axiosInstance.post(
      `${API_ENDPOINTS.QUERIES}/GetQueryByLeadId`,
      requestBody,
      {
        headers: {
          'Content-Type': 'application/json',
        },
      }
    );
    const responseData = response.data;

    // Parse the response based on the API structure (case-insensitive check for status)
    if (responseData.statusCode === 200 && responseData.status?.toLowerCase() === "success") {
      // Parse the data string if it's a JSON string
      let queriesData = [];
      if (typeof responseData.data === "string") {
        try {
          queriesData = JSON.parse(responseData.data);
        } catch (parseError) {
          console.error("Error parsing queries data:", parseError);
          queriesData = [];
        }
      } else if (Array.isArray(responseData.data)) {
        queriesData = responseData.data;
      }

      return {
        success: true,
        data: queriesData,
        message: responseData.message,
        totalRecords: responseData.totalRecords || queriesData.length,
      };
    } else {
      throw new Error(responseData.message || "Failed to fetch queries by lead ID");
    }
  } catch (error) {
    console.error("Error fetching queries by lead ID:", error);

    // Handle different types of errors
    let errorMessage = "Failed to fetch queries by lead ID";

    if (error.response) {
      // Server responded with error status
      const status = error.response.status;
      const serverMessage =
        error.response.data?.message || error.response.data?.error;

      if (status >= 400 && status < 500) {
        // Client errors (4xx)
        errorMessage = serverMessage || `Client error: ${status}`;
      } else if (status >= 500) {
        // Server errors (5xx)
        errorMessage = serverMessage || `Server error: ${status}`;
      }
    } else if (error.request) {
      // Network error - no response received
      errorMessage = "Network error: Unable to connect to server";
    } else {
      // Other errors (validation, parsing, etc.)
      errorMessage = error.message || "Unknown error occurred";
    }

    return {
      success: false,
      error: errorMessage,
      statusCode: error.response?.status || null,
      data: [],
      totalRecords: 0,
    };
  }
};

// Add these functions to your existing queryService.js

// Get all users for assignment
export const getAllUsers = async () => {
  try {
    const response = await api.get('/users'); // Adjust endpoint as needed
    return {
      success: true,
      data: response.data,
      totalRecords: response.data?.length || 0
    };
  } catch (error) {
    return {
      success: false,
      error: error.response?.data?.message || error.message || 'Failed to fetch users'
    };
  }
};

// Reassign query to a different user
export const reassignQuery = async (reassignmentData) => {
  try {
    // Get CompanyId from JWT token
    const companyId = getCompanyIdFromToken();

    // Prepare the payload according to the API specification
    const apiPayload = {
      queryId: reassignmentData.queryId,
      newAssignedTo: reassignmentData.newAssignedUserId, // Map from frontend field name
      remarks: reassignmentData.remarks,
      ...(companyId && { companyId }), // Include companyId if available
    };

    // Make API call using axios instance with POST method
    const response = await axiosInstance.post(
      `${API_ENDPOINTS.QUERIES}/ReassignQuery`,
      apiPayload,
      {
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
      },
    );

    // Validate response status
    if (response.status < 200 || response.status >= 300) {
      throw new Error(
        `HTTP Error: ${response.status} - ${response.statusText}`,
      );
    }

    // Validate response data structure
    if (!response.data) {
      throw new Error("Invalid response: No data received");
    }

    const { data, message, statusCode, status, totalRecords } = response.data;

    // Validate expected response structure (case-insensitive check for status)
    if (statusCode !== 200 || (status?.toLowerCase() !== "success")) {
      throw new Error(message || "API returned unsuccessful status");
    }

    // Parse the data field from string to JSON if needed
    let parsedData = null;
    try {
      parsedData = typeof data === "string" ? JSON.parse(data) : data;
    } catch (parseError) {
      console.error("Error parsing reassignment response data:", parseError);
      // Don't throw error for parsing issues, just use raw data
      parsedData = data;
    }

    return {
      success: true,
      data: parsedData,
      message: message || "Query reassigned successfully",
      totalRecords: totalRecords || 1,
    };
  } catch (error) {
    console.error("Error reassigning query:", error);

    // Handle different types of errors
    let errorMessage = "Failed to reassign query";

    if (error.response) {
      // Server responded with error status
      const status = error.response.status;
      const serverMessage =
        error.response.data?.message || error.response.data?.error;

      if (status >= 400 && status < 500) {
        // Client errors (4xx)
        errorMessage = serverMessage || `Client error: ${status}`;
      } else if (status >= 500) {
        // Server errors (5xx)
        errorMessage = serverMessage || `Server error: ${status}`;
      }
    } else if (error.request) {
      // Network error - no response received
      errorMessage = "Network error: Unable to connect to server";
    } else {
      // Other errors (validation, parsing, etc.)
      errorMessage = error.message || "Unknown error occurred";
    }

    return {
      success: false,
      error: errorMessage,
      data: null,
    };
  }
};

// Create a new query response
export const createQueryResponse = async (queryId, message) => {
  try {
    // Validate required parameters
    if (!queryId) {
      throw new Error("Query ID is required");
    }
    if (!message?.trim()) {
      throw new Error("Message is required");
    }

    // Get CompanyId from JWT token
    const companyId = getCompanyIdFromToken();

    // Prepare the payload according to the API specification
    const apiPayload = {
      QueryId: queryId,
      Message: message.trim(),
      ...(companyId && { companyId }), // Include companyId if available
    };

    // Make API call using axios instance with POST method
    const response = await axiosInstance.post(
      `${API_ENDPOINTS.QUERIES}/QueryResponses`,
      apiPayload,
      {
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
      },
    );

    // Validate response status
    if (response.status < 200 || response.status >= 300) {
      throw new Error(
        `HTTP Error: ${response.status} - ${response.statusText}`,
      );
    }

    // Validate response data structure
    if (!response.data) {
      throw new Error("Invalid response: No data received");
    }

    const { data, message: responseMessage, statusCode, status, totalRecords } = response.data;

    // Validate expected response structure (case-insensitive check for status)
    if (statusCode !== 200 || (status?.toLowerCase() !== "success")) {
      throw new Error(responseMessage || "API returned unsuccessful status");
    }

    // Parse the data field from string to JSON if needed
    let parsedData = null;
    try {
      parsedData = typeof data === "string" ? JSON.parse(data) : data;
    } catch (parseError) {
      console.error("Error parsing query response data:", parseError);
      // Don't throw error for parsing issues, just use raw data
      parsedData = data;
    }

    return {
      success: true,
      data: parsedData,
      message: responseMessage || "Query response created successfully",
      totalRecords: totalRecords || 1,
    };
  } catch (error) {
    console.error("Error creating query response:", error);

    // Handle different types of errors
    let errorMessage = "Failed to create query response";

    if (error.response) {
      // Server responded with error status
      const status = error.response.status;
      const serverMessage =
        error.response.data?.message || error.response.data?.error;

      if (status >= 400 && status < 500) {
        // Client errors (4xx)
        errorMessage = serverMessage || `Client error: ${status}`;
      } else if (status >= 500) {
        // Server errors (5xx)
        errorMessage = serverMessage || `Server error: ${status}`;
      }
    } else if (error.request) {
      // Network error - no response received
      errorMessage = "Network error: Unable to connect to server";
    } else {
      // Other errors (validation, parsing, etc.)
      errorMessage = error.message || "Unknown error occurred";
    }

    return {
      success: false,
      error: errorMessage,
      data: null,
    };
  }
};

// Get smart widgets counter data for dashboard
export const getSmartWidgetsCounter = async () => {
  try {
    // Get CompanyId from JWT token
    const companyId = getCompanyIdFromToken();

    // Make API call using axios instance with GET method
    const response = await axiosInstance.get(
      `${API_ENDPOINTS.QUERIES}/SmartWidgetsCounter`,
      {
        params: {
          ...(companyId && { companyId }), // Include companyId if available
        },
      },
    );
    const responseData = response.data;

    // Parse the response based on the API structure (case-insensitive check for status)
    if (responseData.statusCode === 200 && responseData.status?.toLowerCase() === "success") {
      // Parse the data string if it's a JSON string
      let widgetsData = null;
      if (typeof responseData.data === "string") {
        try {
          widgetsData = JSON.parse(responseData.data);
          // Extract the first object from array if it's an array
          if (Array.isArray(widgetsData) && widgetsData.length > 0) {
            widgetsData = widgetsData[0];
          }
        } catch (parseError) {
          console.error("Error parsing widgets data:", parseError);
          widgetsData = null;
        }
      } else if (responseData.data) {
        widgetsData = responseData.data;
      }

      return {
        success: true,
        data: widgetsData,
        message: responseData.message,
        totalRecords: responseData.totalRecords,
      };
    } else {
      throw new Error(
        responseData.message || "Failed to fetch smart widgets counter",
      );
    }
  } catch (error) {
    console.error("Error fetching smart widgets counter:", error);

    return {
      success: false,
      error: error.message || "Failed to fetch smart widgets counter",
      data: null,
      totalRecords: 0,
    };
  }
};

// Get user query count data
export const getUserQueryCount = async () => {
  try {
    // Get CompanyId from JWT token
    const companyId = getCompanyIdFromToken();

    // Make API call using axios instance with GET method
    const response = await axiosInstance.get(
      `${API_ENDPOINTS.QUERIES}/GetUserQueryCount`,
      {
        params: {
          ...(companyId && { companyId }), // Include companyId if available
        },
      },
    );
    const responseData = response.data;

    // Parse the response based on the API structure (case-insensitive check for status)
    if (responseData.statusCode === 200 && responseData.status?.toLowerCase() === "success") {
      // Parse the data string if it's a JSON string
      let userQueryData = [];
      if (typeof responseData.data === "string") {
        try {
          userQueryData = JSON.parse(responseData.data);
        } catch (parseError) {
          console.error("Error parsing user query count data:", parseError);
          userQueryData = [];
        }
      } else if (Array.isArray(responseData.data)) {
        userQueryData = responseData.data;
      }

      return {
        success: true,
        data: userQueryData,
        message: responseData.message,
        totalRecords: responseData.totalRecords || userQueryData.length,
      };
    } else {
      throw new Error(
        responseData.message || "Failed to fetch user query count",
      );
    }
  } catch (error) {
    console.error("Error fetching user query count:", error);

    return {
      success: false,
      error: error.message || "Failed to fetch user query count",
      data: [],
      totalRecords: 0,
    };
  }
};
